/*=== CORE IMPORTS ===*/
import { html } from 'lit';
import { map } from 'lit/directives/map.js';
import { BaseLogItemDetailView } from './base-log-item-detail-view.js';
import { logItemEditViewStyles } from './log-item-edit-view-styles.js';
import { FormHostMixin } from '../../modules/form-host-mixin.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/avatar/avatar.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/color-picker/color-picker.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import '@shoelace-style/shoelace/dist/components/radio-button/radio-button.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

/*=== Q1 COMPONENTS ===*/
import '../../components/q1-md-textarea/q1-md-textarea.js';

/*=== Q1 MODULES ===*/
import { q1DocFunctions, q1LogItemFunctions } from '../../modules/q1-core-functions.js';
import {
  dateWithCurrentTime, getMostRecentMonday, isoToLocalDateString,
} from '../../modules/date-client-helpers.js';
import {
  logItemRateLimitDetails, getLogItemPublishDateAllowed,
} from '../../modules/log-item-helpers-client.js';

/*=== CONSTANTS ===*/

import {
  logItemStatusDefault,
  logItemVisibilityDefault,
  logItemVisibilityOptions_guilds,
  logItemVisibilityOptions_projects,
} from '../../models/log-item-client-enums.js';

import {
  logItemStatusActionsMap,
} from '../../models/log-item-client-actions.js'

/*=== CLASS DEFINITION ===*/

export class LogItemEditView extends FormHostMixin(BaseLogItemDetailView) {
  static styles = [
    ...super.styles,
    logItemEditViewStyles,
  ];

  static properties = {
    ...super.properties,

    logItemStats: { type: Object },

    authors: { type: Array },
    status: { type: String },
    visibility: { type: String },
    headline: { type: String },
    body: { type: String },
    externalAttachments: { type: Array },
    publishDate: { type: String },

    visibilityOptions: { type: Array },
  };

  constructor() {
    super();

    this.logItemStats = null;

    this.authors = [];
    this.status = logItemStatusDefault;
    this.visibility = logItemVisibilityDefault;
    this.headline = '';
    this.body = '';
    this.externalAttachments = [];
    this.publishDate = null;
  }

  /*=== LIFECYCLE & INITIALIZATION METHODS ===*/
  
  async logItemLoaded() {
    // ES1: Current member cannot edit this log item
    if (!this.context.canEdit) {
      this.appElement.showToast(
        'You do not have permission to edit this log item.',
        'Not authorized to edit.',
        'danger'
      );
      this.navigateTo(`/li/${this.logItemId}`);
      return null;
    }

    // ES2: Detect invalid status for edit action
    const editActionSettings = logItemStatusActionsMap['edit'];
    if (!editActionSettings?.availableStati?.includes(this.logItem?.status)) {
      this.appElement.showToast(
        'This log item cannot be edited in its current status.',
        'Log item not editable.',
        'danger'
      );
      this.navigateTo(`/li/${this.logItemId}`);
      return null;
    }

    // Get the log item history (note: errors are handled in the base class)
    const { getHistory } = q1LogItemFunctions;
    const historyResponse = await getHistory({
      subjectId: this.subjectId,
      subjectCollection: this.logItem.subjectCollection,
    });
    this.logItemStats = historyResponse.data?.stats || {};

    this.setViewMeta();

    this.authors = this.logItem.authors || [];
    this.status = this.logItem.status;
    this.visibility = this.logItem.visibility;
    this.headline = this.logItem.headline || '';
    this.body = this.logItem.body || '';
    this.externalAttachments = this.logItem.externalAttachments || [];

    this.publishDate = this.logItem.publishedAt?.includes('T')
      ? isoToLocalDateString(this.logItem.publishedAt)
      : new Date().toLocaleDateString('en-CA');

    this.visibilityOptions = this.logItem.subjectCollection === 'guilds'
      ? logItemVisibilityOptions_guilds
      : logItemVisibilityOptions_projects;
  }

  /*=== ACTIONS & EVENT HANDLERS ===*/

  cancelEdit() {
    if (!confirm(
      "Are you sure you want to discard your changes?\n\n"
      + "You will lose any unsaved changes to this log item."
    )) return null;

    this.navigateTo(`/li/${this.logItemId}`);
  }

  saveAsDraft() {
    this.status = 'draft';
    this._updateLogItem();
  }

  saveAndPublish() {
    if (!confirm(
      "Please confirm that you're ready to publish this update.\n\n"
      + "You can still edit the log item after publishing, but publishing will push it out "
      + "to all relevant communications channels."
    )) return null;

    this.status = 'published';
    this._updateLogItem();
  }
  
  _updateLogItem() {
    const updateLogItemForm = this.renderRoot.querySelector('#update-form');
    
    if (!updateLogItemForm.reportValidity()) {
      this.showToast('Please fix the form validation errors to continue', null, 'danger');
      return;
    }
    
    const { updateDoc } = q1DocFunctions;
    this.loading = true;
    this.error = false;
    this.errorMessage = '';

    const docUpdate = {
      authors: this.authors,
      status: this.status,
      visibility: this.visibility,
      headline: this.headline,
      body: this.body,
      externalAttachments: this.externalAttachments,
    };
    // Only add the publishedAt date if the log item is being published OR being saved as draft
    if (this.logItem.status === 'draft') {
      docUpdate.publishedAt = this.publishedAt;
    }
    
    updateDoc({
      docCollection: 'logItems',
      docId: this.logItemId,
      docUpdate,
    }).then((response) => {
      this.showToast(null, 'Log item updated successfully!', 'success');
      
      this.navigateTo(`/li/${this.logItemId}`);
    }).catch((error) => {
      console.error('Error calling function:', error);
      this.showToast(error.message, 'There was an error updating your log item!', 'danger');
      this.errorMessage = error.message;
    }).finally(() => {
      this.loading = false;
    });
  }

  /** Renders the selected item tag for the author multi-select. */
  getAuthorTag(option, index) {
    // Use the same avatar settings
    const avatarImage = option.querySelector('sl-avatar[slot="prefix"]').image;
    const textLabel = option.getTextLabel();

    return html`
      <sl-tag removable>
        <sl-avatar
          image=${avatarImage}
          style="--size: 1rem; display: inline-flex; margin-right: 0.5rem;"
        ></sl-avatar>
        ${textLabel}
      </sl-tag>
    `;
  }

  /*=== GETTERS ===*/

  get publishedAt() {
    return this.publishDate ? dateWithCurrentTime(this.publishDate) : new Date();
  }

  get canPublish() {
    if (!this.isDraft) return true; // Only check publish date if the log item is a draft

    const subjectActivationStatus = this.subject?.activationStatus || 'created';
    const {
      isAllowed, errorMessage,
    } = getLogItemPublishDateAllowed(subjectActivationStatus, this.logItemStats, this.publishedAt);
    
    return isAllowed;
  }

  get publishDateNotes() {
    const now = new Date();
    const publishedAt = this.publishedAt;
    const todayDateString = new Date().toLocaleDateString('en-CA');
    const mostRecentMonday = getMostRecentMonday();

    // Scenario 1: Future date
    if (publishedAt > now) return html`
      <strong>You've selected a future date.</strong>
      You can save as draft, but you won't be able to publish it with a future date.
    `;

    const subjectActivationStatus = this.subject?.activationStatus || 'created';
    const {
      currentUsageMessage, postingTodayError, postingThisWeekError,
    } = logItemRateLimitDetails(subjectActivationStatus, this.logItemStats);

    // Scenario 2: Today's date
    if (this.publishDate === todayDateString) return html`
      ${!postingTodayError ? html`
        <strong>You've selected today's date.</strong>
        This post will trigger notifications to your members/followers.
        (BETA NOTE: Log item notifications aren't implemented yet. Coming soon!)
      ` : html`
        <strong>${postingTodayError}</strong>
      `}
      <br/><br/>
      ${currentUsageMessage}
    `;

    // Scenario 3: Past date, this week
    if (publishedAt > mostRecentMonday) return html`
      ${!postingThisWeekError ? html`
        <strong>You've selected a date earlier in this week.</strong>
        Posting back-dated updates is great for back-filling your log.
        Note that back-dated posts do not trigger notifications to your members/followers.
      ` : html`
        <strong>${postingThisWeekError}</strong>
      `}
      <br/><br/>
      ${currentUsageMessage}
    `;
    
    // Scenario 4: Past date, prior week
    return html`
      <strong>
        You've selected a date prior to this week. This won't impact your weekly limit.
      </strong>
      Posting back-dated updates is great for back-filling your log.
      Note that back-dated posts do not trigger notifications to your members/followers.
    `;
  }

  /*=== RENDER TEMPLATES ===*/

  get contentHeaderTemplate() {
    if (['init'].includes(this.viewMode)) return null;

    return html`
      <header-bar class="small">
        ${this.subject && this.logItem ? html`

          <a
            href=${this.subject.detailViewPath}
            class="avatar-link"
            alt=${this.subject.name}
          >
            <sl-avatar
              image=${this.subject.avatar?.small?.url}
              label=${this.subject.name}
              shape=${this.subject.avatar_shape}
            >
              <sl-icon slot="icon" library="material" name=${this.subject.avatar_icon}></sl-icon>
            </sl-avatar>
          </a>
          
          <h1>
            <a class="prefix" href=${this.subject.detailViewPath}>${this.subject.name}</a>
            Edit ${this.logItem.subjectCollection_titleCaseSingular} Log Update
          </h1>

        ` : html`

            <h1>
              <span class="prefix">Log Item View</span>
              Log Item Id: ${this.logItemId}
            </h1>

        `}
      </header-bar>
    `;
  }

  get viewModeTemplate_loaded() {
    return html`
      ${!this.error ? '' : html`
        <sl-alert variant="danger" open>
          <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
          ${this.errorMessage}
        </sl-alert>
      `}

      <log-item-content class="sl-themed-secondary">
        <div class="top-line">
          <sl-icon name="info" library="material"></sl-icon>
          <p class="subtitle">
            The ${this.logItem.subjectCollection_singular} log helps you record the ongoing 
            progress of the ${this.logItem.subjectCollection_singular}. Regular updates keep your 
            community informed, but it's important to balance the frequency of updates with the 
            quality of content.
            <br/><br/>
          </p>
          ${!this.isDraft ? '' : html`
            <p class="subtitle">
              Each new update will notify your members/followers according to their preferences.
            </p>
          `}
        </div>

        <sl-divider class="full-width"></sl-divider>

        <form id="update-form" class="q1 sl-themed-secondary">
          <sl-input
            id="headline"
            name="headline"
            label="Headline"
            value=${this.headline}
            @sl-change=${this.handleChange}
            maxlength="250"
            ?disabled=${this.loading}
            required
          ></sl-input>
  
          <q1-md-textarea
            id="body"
            name="body"
            label="Body"
            value=${this.body}
            @change=${this.handleChange}
            maxlength="25000"
            rows="20"
            resize="auto"
            ?disabled=${this.loading}
            required
          ></q1-md-textarea>
  
          <fieldset-panel>
            ${!this.isDraft ? '' : html`
              <sl-input
                id="publishDate"
                name="publishDate"
                label="Publish Date"
                type="date"
                value=${this.publishDate}
                @sl-change=${this.handleChange}
                ?disabled=${this.loading}
                class="label-on-left"
                required
              ></sl-input>

              <p id="publishDateNotes">${this.publishDateNotes}</p>
            `}

            <sl-select 
              id="authors"
              name="authors"
              label="Authors"
              value=${this.authors?.join(' ')}
              class="label-on-left"
              @sl-change=${this.handleChange}
              .getTag=${this.getAuthorTag}
              help-text="Which organizers are listed as authors of this update"
              ?disabled=${this.loading}
              multiple clearable required
            >
              ${this.organizerMembers.map(member => html`
                <sl-option value=${member.ref}>
                  <sl-avatar
                    image=${member.profile?.mini?.url}
                    slot="prefix"
                  ></sl-avatar>
                  ${member.nameInternal}
                </sl-option>
              `)}
            </sl-select>
  
            <sl-radio-group
              id="visibility"
              name="visibility"
              label="Visibility"
              value=${this.visibility}
              class="label-on-left wrap-button-group"
              @sl-change=${this.handleChange}
              help-text="Who can see this log item?"
              required
            >
              ${map(this.visibilityOptions, (o) => html`
                <sl-tooltip content=${o.description}>
                  <sl-radio-button value=${o.value} ?disabled=${this.loading}>
                    <span class="hide-until-bp2">
                      <sl-icon
                        class="faux-prefix"
                        name=${o.icon}
                        library=${o.iconLibrary}
                      ></sl-icon>
                      ${o.label}
                    </span>
                    <span class="hide-at-bp2">
                      <sl-icon name=${o.icon} library=${o.iconLibrary}></sl-icon>
                    </span>
                  </sl-radio-button>
                </sl-tooltip>
              `)}
            </sl-radio-group>
          </fieldset-panel>
          
          <button-bar class="right-align">
            <sl-button
              size="large"
              @click=${this.cancelEdit}
              ?disabled=${this.loading}
            >
              ${this.loading ? html`
                <sl-spinner slot="prefix"></sl-spinner>
                Saving...
              ` : html`
                <sl-icon slot="prefix" library="material" name="close"></sl-icon>
                Discard Changes
              `}
            </sl-button>

            ${this.logItem.status === 'published' ? null : html`
              <sl-button
                size="large"
                @click=${this.saveAsDraft}
                ?disabled=${this.loading}
              >
                ${this.loading ? html`
                  <sl-spinner slot="prefix"></sl-spinner>
                  Saving...
                ` : html`
                  <sl-icon slot="prefix" library="material" name="draft"></sl-icon>
                  Save Draft
                `}
              </sl-button>
            `}
  
            <sl-button
              variant="primary"
              size="large"
              @click=${this.saveAndPublish}
              ?disabled=${this.loading || !this.canPublish}
            >
              ${this.loading ? html`
                <sl-spinner slot="prefix"></sl-spinner>
                Saving...
              ` : html`
                <sl-icon slot="prefix" library="material" name="send"></sl-icon>
                Save & Publish Changes
              `}
            </sl-button>
          </button-bar>
        </form>
      </log-item-content>
    `;
  }

}

customElements.define('log-item-edit-view', LogItemEditView);
